import * as React from 'react';
import { Messages } from '../../../codegen/Localize';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { UserMapper } from '../../../ts/mappers/UserMapper';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { IDataConnectionInfo } from '../../../ts/models/IDataConnectionInfo';
import { DataSources } from '../../../ts/resources/Datasources';
import { DetailsTooltipStyles } from '../CardView/DetailsTooltipStyles';
import { getCommonDatasourceInfo } from '../CommonDatasourceInfo';
import { ConnectionDisplay } from '../ListView/DataConnectionComponents';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { EntityLinkProps } from './Elements/EntityLink';
import { MarkupField } from './Elements/MarkupField';
import { Name } from './Elements/Name';
import { RelatedEntity } from './Elements/RelatedEntity';
import { RelatedEntityList } from './Elements/RelatedEntityList';
import { Tags } from './Elements/Tags';
import { Tooltip } from './Elements/Tooltip';
import { User } from './Elements/User';
import { ContentTooltipProps, TooltipDescriptionTestId } from './TooltipHelper';

export function DatasourceTooltip(props: ContentTooltipProps): JSX.Element {
  const relatedConnections = getRelatedConnectionLinkProps(props.content, props.urls);
  const extraDetail = getCommonDatasourceInfo(props.content);
  const certDetails = extraDetail ? (extraDetail.certificationDetails || null) : null;
  const hasCertDetails = certDetails;
  const isPublished = DataSources.isPublished(extraDetail);
  const isEmbedded = DataSources.isEmbedded(extraDetail);

  const workbookLink: EntityLinkProps = extraDetail.workbook ? {
    href: props.urls.exploreWorkbookUrl(extraDetail.workbook.id),
    iconImagePath: imgPath('workbook_content_type_small.svg'),
    iconHeightOverride: 18,  // the icon height is 18 just for workbooks :|
    name: extraDetail.workbook.name
  } : null;

  return (
    <Tooltip>
      <Name name={props.content.name} />
      <MarkupField markup={props.content.description}
                   testId={TooltipDescriptionTestId}
                   extraClasses={[DetailsTooltipStyles.MetadataSectionWithContentDescription]} />
      <Tags tags={props.content.tags} />
      <RelatedEntityList label={Messages.column_connects_to()} list={relatedConnections} />
      { isEmbedded && <RelatedEntity label={Messages.datasourceCardWorkbook()} link={workbookLink} />}
      { isPublished && <User user={props.content.owner} label={Messages.owner()} urls={props.urls} /> }
      { hasCertDetails && hasCertDetails.isCertified && (
        <div className={`${DetailsTooltipStyles.MetadataSection} ${DetailsTooltipStyles.MetadataSectionWithCertifierDetails}`}>
          <User user={UserMapper.mapOne(certDetails.certifier)} label={Messages.datasourceCardCertifier()} urls={props.urls} />
          <MarkupField label={Messages.dataSourceCertification_notes()} markup={certDetails.certificationNote} />
        </div>
      )}
    </Tooltip>
  );
}

function getRelatedConnectionLinkProps(datasource: IContentInfo, urls: SpacesSiteUrls): EntityLinkProps[] {
  if (datasource && datasource.hasOwnProperty('connections')) {
    const connections: IDataConnectionInfo[] = (datasource as any).connections;
    return connections.map(c => {
      const displayMetadata = ConnectionDisplay.getDisplayMetadata(c, urls);
      const translatedText = ConnectionDisplay.translatedText(displayMetadata);
      return {
        name: translatedText.connectionName,
        href: displayMetadata.href,
        iconImagePath: displayMetadata.iconImageUrl
      };
    });
  } else {
    return [];
  }
}
